import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Vehicle } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { Alert, Button, ConfirmDiscardModal, Spinner, Tabs, Tab } from '@/components/ui';
import EquipmentUnitHeader from '../components/EquipmentUnitHeader';
import EquipmentAboutTab from '../components/EquipmentAboutTab';
import EquipmentRatesTab from '../components/EquipmentRatesTab';
import EquipmentMaintenanceTab from '../components/EquipmentMaintenanceTab';
import EquipmentDefectsTab from '../components/EquipmentDefectsTab';
import EquipmentRepairHistoryTab from '../components/EquipmentRepairHistoryTab';
import EquipmentPmHistoryTab from '../components/EquipmentPmHistoryTab';
import EquipmentUnitActions from '../components/EquipmentUnitActions';
import EquipmentAuditHistoryTab from '../components/EquipmentAuditHistoryTab';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { EQUIPMENT_PERMISSIONS } from '../utils/equipmentPermissions';
import EquipmentAttachmentsTab from '../components/EquipmentAttachmentsTab';
import TransferPendingBanner from '../components/TransferPendingBanner';
import { vehicleTransferService, type Transfer } from '../../../services/vehicleTransferService';
import { notifyApprovalsChanged } from '../../../components/awaitingApprovalsCount';

const TAB_KEYS = ['about', 'rates', 'defects', 'maintenance', 'pm-history', 'repair', 'attachments', 'history'] as const;
/** EQ-04: tabs that hold repair records need View Repairs. */
const REPAIR_TABS = new Set(['defects', 'pm-history', 'repair']);

const EquipmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [aboutDirty, setAboutDirty] = useState(false);
  const [ratesDirty, setRatesDirty] = useState(false);
  const [openTransfer, setOpenTransfer] = useState<Transfer | null>(null);
  const { hasPermission } = usePermissions();
  const canSeeTransfers = hasPermission(EQUIPMENT_PERMISSIONS.vehicleView) || hasPermission(EQUIPMENT_PERMISSIONS.transferServiceArea);
  const canViewRepairs = hasPermission(EQUIPMENT_PERMISSIONS.repairsView);
  const canManageRepairs = hasPermission(EQUIPMENT_PERMISSIONS.repairsManage);
  const unsaved = useUnsavedChangesGuard(aboutDirty || ratesDirty);
  useDocumentTitle(vehicle ? `${vehicle.unitNumber} – Equipment` : 'Equipment');

  const fetchOpenTransfer = useCallback(async () => {
    const numericId = Number(id);
    if (!canSeeTransfers || !Number.isInteger(numericId)) return;
    try {
      const open = await vehicleTransferService.list('open', numericId);
      setOpenTransfer(open[0] ?? null);
    } catch {
      // The banner is a convenience; the unit page still works without it.
      setOpenTransfer(null);
    }
  }, [id, canSeeTransfers]);

  useEffect(() => {
    void fetchOpenTransfer();
  }, [fetchOpenTransfer]);

  /**
   * Loads the unit. Only the first load shows the page spinner; later refreshes (after a defect
   * or odometer change) keep the page on screen and swap the record in (EQ-06, STATE-01).
   */
  const fetchVehicle = useCallback(async () => {
    const numericId = id ? parseInt(id, 10) : NaN;
    if (Number.isNaN(numericId)) {
      setError('This unit link is not valid.');
      setLoading(false);
      return;
    }
    try {
      setVehicle(await vehicleService.getVehicleById(numericId));
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error && /not found/i.test(err.message)
          ? 'This unit was not found, or you do not have access to it.'
          : 'Could not load this unit. Try again, or contact support if it keeps happening.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void fetchVehicle();
  }, [fetchVehicle]);

  const tabParam = searchParams.get('tab') || 'about';
  const allowedTab = TAB_KEYS.includes(tabParam as (typeof TAB_KEYS)[number]) && (canViewRepairs || !REPAIR_TABS.has(tabParam));
  const activeTab = allowedTab ? tabParam : 'about';

  if (loading) {
    return (
      <div className="tw-w-full tw-p-4">
        <div className="tw-py-5 tw-text-center">
          <Spinner role="status">
            <span className="tw-sr-only">Loading…</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="tw-w-full tw-p-4">
        <Alert variant="danger" className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <span>{error || 'This unit was not found.'}</span>
          <span className="tw-flex tw-gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setLoading(true);
                void fetchVehicle();
              }}
            >
              Retry
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => navigate('/equipment')}>
              Back to Equipment
            </Button>
          </span>
        </Alert>
      </div>
    );
  }

  const canEditUnit = hasPermission(EQUIPMENT_PERMISSIONS.vehicleEdit);
  const canEditRoadClass = hasPermission(EQUIPMENT_PERMISSIONS.editRoadClass);
  const canEdit = canEditUnit || canEditRoadClass || canManageRepairs;
  const defectCount = vehicle.openDefectCount ?? 0;

  const stopEditing = () => {
    setAboutDirty(false);
    setEditing(false);
  };

  return (
    <div className="tw-w-full tw-p-4">
      <Link
        to="/equipment"
        className="tw-mb-3 tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-content-secondary tw-no-underline hover:tw-text-content"
      >
        <i className="bi bi-arrow-left" aria-hidden="true" />
        Back to Equipment
      </Link>

      <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-4 tw-shadow-card lg:tw-flex-row lg:tw-items-start lg:tw-justify-between">
        <EquipmentUnitHeader vehicle={vehicle} canEditPhoto={editing && canEditUnit} onUpdated={setVehicle} />
        <div className="tw-min-w-0 lg:tw-max-w-[60%] lg:tw-flex-1">
          <EquipmentUnitActions
            vehicle={vehicle}
            canEdit={canEdit}
            editing={editing}
            canSetAvailability={hasPermission(EQUIPMENT_PERMISSIONS.availabilitySet)}
            canSetFleetStatus={hasPermission(EQUIPMENT_PERMISSIONS.fleetStatusSet)}
            canTransferForemanArea={hasPermission(EQUIPMENT_PERMISSIONS.transferForemanArea)}
            canTransferServiceArea={hasPermission(EQUIPMENT_PERMISSIONS.transferServiceArea)}
            // Hidden for now (decision 2026-11-03): every service area move goes through a transfer.
            canMoveServiceAreaNow={false}
            openTransferId={openTransfer?.id ?? null}
            onTransferRequested={(transfer) => {
              setOpenTransfer(transfer);
              notifyApprovalsChanged();
            }}
            onEdit={() => {
              setEditing(true);
              if (activeTab !== 'about') {
                const next = new URLSearchParams(searchParams);
                next.set('tab', 'about');
                setSearchParams(next, { replace: true });
              }
            }}
            onUpdated={setVehicle}
          />
        </div>
      </div>

      {openTransfer && <TransferPendingBanner transfer={openTransfer} />}

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => {
          if (!key || key === activeTab) return;
          unsaved.guard(() => {
            if (aboutDirty || editing) stopEditing();
            setRatesDirty(false);
            const next = new URLSearchParams(searchParams);
            next.set('tab', key);
            setSearchParams(next, { replace: true });
          });
        }}
        id="equipment-unit-tabs"
      >
        <Tab eventKey="about" title="About">
          <EquipmentAboutTab
            vehicle={vehicle}
            canEditUnit={canEditUnit}
            canEditRoadClass={canEditRoadClass}
            canEditCvip={canManageRepairs}
            editing={editing}
            onUpdated={setVehicle}
            onCancel={() => unsaved.guard(stopEditing)}
            onSaved={stopEditing}
            onDirtyChange={setAboutDirty}
          />
        </Tab>
        <Tab eventKey="rates" title="Rates">
          <EquipmentRatesTab
            vehicle={vehicle}
            canEditRate={hasPermission(EQUIPMENT_PERMISSIONS.editRate)}
            onUpdate={setVehicle}
            onDirtyChange={setRatesDirty}
          />
        </Tab>
        {canViewRepairs && (
          <Tab eventKey="defects" title={`Defects (${defectCount})`}>
            <EquipmentDefectsTab vehicle={vehicle} canManage={canManageRepairs} onUpdated={fetchVehicle} />
          </Tab>
        )}
        <Tab eventKey="maintenance" title="Maintenance">
          <EquipmentMaintenanceTab vehicle={vehicle} canCorrectOdometer={canManageRepairs} onUpdated={fetchVehicle} />
        </Tab>
        {canViewRepairs && (
          <Tab eventKey="pm-history" title="PM History">
            <EquipmentPmHistoryTab vehicle={vehicle} />
          </Tab>
        )}
        {canViewRepairs && (
          <Tab eventKey="repair" title="Repair History">
            <EquipmentRepairHistoryTab vehicleId={vehicle.id} vehicle={vehicle} />
          </Tab>
        )}
        <Tab eventKey="attachments" title="Attachments">
          <EquipmentAttachmentsTab vehicle={vehicle} />
        </Tab>
        <Tab eventKey="history" title="Audit History">
          <EquipmentAuditHistoryTab vehicle={vehicle} />
        </Tab>
      </Tabs>

      <ConfirmDiscardModal
        show={unsaved.pending}
        subject={vehicle.unitNumber}
        onDiscard={unsaved.discard}
        onKeepEditing={unsaved.keepEditing}
      />
    </div>
  );
};

export default EquipmentDetailPage;
